import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useSnackbar } from 'notistack';
import {
  Typography,
  Box,
  Select,
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  ArrowForwardIos, LocalLibrary, Search,
} from '@mui/icons-material';
import CustomTable, { type TableColumnHeader } from '../../components/General/CustomTable';
import Header from '../../components/Layout/Header';
import useStyles from './styles';
import Page from '../../components/Layout/Page';
import { getEnrollmentStudents } from '../../requests/api/enrollment';
import { getLocations } from '../../requests/api/sections';
import handleApiResponse from '../../utils/handleApiResponse';
import { getDateOnlyFormat } from '../../utils/helpers';
import { useYear } from '../../hooks/useYear';

type EnrollmentRow = {
  id: number;
  name: string;
  lastname: string;
  rut: string;
  phoneNumber: string;
  email: string;
  stage: string;
  stageUpdate: string;
  allFeePaymentsComplete?: boolean;
};

function RegisterTable() {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [status, setStatus] = useState<string>('all');
  const [name, setName] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [locations, setLocations] = useState<{ id: number; name: string }[]>([]);

  const stageToColor: Record<string, string> = {
    enrollment: '#E94745',
    'study-plan': '#EE803A',
    schedule: '#F2AC32',
    enrolled: '#B0AB26',
    paid: '#21A80D',
    comments: '#CC9710',
    closed: '#9E9E9E',
  };

  const stageToLabel: Record<string, string> = {
    incomplete: 'Registro incompleto',
    enrollment: 'Inscripción',
    'study-plan': 'Plan de estudio',
    schedule: 'Horario',
    enrolled: 'Matriculado',
    paid: 'Pagado',
    comments: 'Comentarios',
    answer_pending: 'Pendiente de respuesta',
    answered: 'Respondido',
    closed: 'Proceso cerrado',
  };

  const headers: TableColumnHeader[] = [
    {
      label: 'Fecha registro',
      key: 'createdAt',
      valueType: 'custom',
      functionToApply: (
        value: unknown,
        _rowIndex: number,
        row: EnrollmentRow,
      ): JSX.Element[] | string | number => {
        const dateText = value ? getDateOnlyFormat(String(value), '/', true) : '-';

        return [
          <Box
            key={`createdAt-wrapper-${row.id}`}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {row.stage === 'answer_pending' && (
              <Box
                key={`pending-dot-${row.id}`}
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  bgcolor: '#E94745',
                  mr: '5px',
                }}
              />
            )}
            <Typography key={`createdAt-${row.id}`} variant="body2" marginLeft={row.stage !== 'answer_pending' ? '13px' : 0}>
              {dateText}
            </Typography>
          </Box>,
        ];
      },
    },
    {
      label: 'Nombre',
      key: 'name',
      valueType: 'custom',
      functionToApply: (
        value: unknown,
        _rowIndex: number,
        row: EnrollmentRow,
      ): JSX.Element[] | string | number => {
        const fullName = `${row.name || ''} ${row.lastname || ''}`.trim() || '-';
        const rut = row.rut || '-';
        return [
          <Box key={`nombre-${row.id}`} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2">
              {fullName}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              {rut}
            </Typography>
          </Box>,
        ];
      },
    },
    { label: 'Teléfono', key: 'phoneNumber', valueType: 'text' },
    { label: 'Email', key: 'email', valueType: 'text' },
    {
      label: 'Sede',
      key: 'location',
      valueType: 'custom',
      functionToApply: (
        value: unknown,
        _rowIndex: number,
        row: EnrollmentRow,
      ): JSX.Element[] | string | number => {
        const locationValue = String(value || '');
        if (!locationValue || locationValue === '-') {
          return '-';
        }
        const locationList = locationValue.split(',').map((loc) => loc.trim()).filter((loc) => loc);
        if (locationList.length === 0) {
          return '-';
        }
        return [
          <Box key={`sede-${row.id}`} sx={{ display: 'flex', flexDirection: 'column' }}>
            {locationList.map((loc, index) => (
              <Typography key={`sede-item-${row.id}-${index}`} variant="body2" sx={{ textAlign: 'left' }}>
                - {loc}
              </Typography>
            ))}
          </Box>,
        ];
      },
    },
    {
      label: 'Estado',
      key: 'stage',
      valueType: 'custom',
      functionToApply: (
        value: unknown,
        _rowIndex: number,
        row: EnrollmentRow,
      ): JSX.Element[] | string | number => {
        const v = String(value || '');
        let label = stageToLabel[v] || v;
        let color = stageToColor[v] || '#9E9E9E';

        if (v === 'enrolled' && row.allFeePaymentsComplete) {
          label = 'Arancel pagado';
          color = '#21A80D';
        }

        return [
          <Typography key={`estado-${v}`} variant="body2" sx={{ color, fontWeight: 600 }}>
            {label}
            {row.stageUpdate && <br />}
            {row.stageUpdate && getDateOnlyFormat(row.stageUpdate, '/', true)}
          </Typography>,
        ];
      },
    },
    {
      label: 'Ver más',
      key: 'actions',
      valueType: 'custom',
      functionToApply: (_value: unknown, rowIndex: number) => [
        <IconButton
          key={`ver-mas-${rowIndex}`}
          size="small"
          color="error"
          onClick={() => {
            const row = rows[rowIndex];
            if (row?.id) {
              history.push(`/tabla-registro/alumno/${row.id}`, { stage: row.stage });
            }
          }}
        >
          <ArrowForwardIos />
        </IconButton>,
      ],
    },
  ];

  const [rows, setRows] = useState<EnrollmentRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [tableParameters, setTableParameters] = useState<{ page: number; rowsPerPage: number }>({
    page: 0,
    rowsPerPage: 10,
  });
  const { selectedYear } = useYear();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await getEnrollmentStudents({
          stage: status,
          name,
          page: tableParameters.page,
          limit: tableParameters.rowsPerPage,
          location: selectedLocation,
          year: selectedYear,
        });
        const students = (resp?.students || []) as Array<{
          id?: number | string;
          createdAt?: string;
          rut?: string;
          phoneNumber?: string;
          stage?: string;
          user?: { name?: string; lastname?: string; email?: string };
          contracts?: Array<{ statusName?: string }>;
          location?: string;
          stageUpdate?: string;
          allFeePaymentsComplete?: boolean;
        }>;
        const mapped: EnrollmentRow[] = students.map((s) => ({
          id: Number(s.id) || 0,
          createdAt: s.createdAt || '',
          name: s.user?.name || '',
          lastname: s.user?.lastname || '',
          rut: s.rut || '',
          phoneNumber: s.phoneNumber || '',
          email: s.user?.email || '',
          location: s.location || '',
          stage: s.stage || (s.contracts?.[0]?.statusName || ''),
          stageUpdate: s.stageUpdate || '',
          allFeePaymentsComplete: s.allFeePaymentsComplete || false,
        }));
        if (mounted) {
          setRows(mapped);
          setTotal(resp?.total ?? mapped.length);
        }
      } catch (e) {
        if (mounted) {
          setRows([]);
          setTotal(0);
        }
      }
    })();

    const fetchLocations = async () => {
      try {
        const response = await getLocations();
        const locationsData = (response.locations || []).filter((l: { name: string }) => !l.name.toLowerCase().includes('online'));
        setLocations(locationsData);
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      }
    };
    fetchLocations();

    return () => { mounted = false; };
  }, [status, name, tableParameters.page, tableParameters.rowsPerPage, selectedLocation]);

  return (
    <Page>
      <Box className={classes.content}>
        <Box className={classes.header}>
          <Header
            title="Alumnos registrados"
            icon={<LocalLibrary fontSize="large" />}
          />
          <Box className={classes.filterContainer}>
            <Select
              value={status}
              size="small"
              className={classes.selectStatus}
              onChange={(e) => setStatus(e.target.value as string)}
            >
              <MenuItem value="all">Todos los estados</MenuItem>
              <MenuItem value="enrollment">Inscripción</MenuItem>
              <MenuItem value="study-plan">Plan de estudio</MenuItem>
              <MenuItem value="enrolled">Matriculado</MenuItem>
              <MenuItem value="comments">Comentarios</MenuItem>
              <MenuItem value="incomplete">Registro incompleto</MenuItem>
            </Select>
            <TextField
              placeholder="Buscar alumno..."
              size="small"
              value={name}
              className={classes.searchField}
              InputProps={{ startAdornment: (<InputAdornment position='start'><Search /></InputAdornment>) }}
              onChange={(e) => setName(e.target.value)}
            />
            <Select
              value={selectedLocation}
              size="small"
              className={classes.selectStatus}
              onChange={(e) => setSelectedLocation(e.target.value as string)}
            >
              <MenuItem value="all">Todas las sedes</MenuItem>
              {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name || ''}</MenuItem>)}
            </Select>
          </Box>
        </Box>
        <CustomTable
          headers={headers}
          data={{ values: rows, count: total }}
          tableParameters={tableParameters}
          setTableParameters={setTableParameters}
        />
      </Box>
    </Page>
  );
}

export default RegisterTable;
